import { useState } from "react";
import { Check, X, AlertTriangle, RotateCcw, Eye } from "lucide-react";
import { fmtDate } from "@/lib/api";

export function FilesView({ files }) {
  const names = Object.keys(files || {});
  const [sel, setSel] = useState(null);
  const cur = sel && files[sel] !== undefined ? sel : names[0];
  if (!names.length) return <Empty>Aún no hay archivos.</Empty>;
  return (
    <div className="h-full flex flex-col" data-testid="files-view">
      <div className="flex gap-1 p-2 border-b border-white/5">
        {names.map((n) => <button key={n} onClick={() => setSel(n)} data-testid={`file-tab-${n}`} className={`px-3 py-1 rounded-md font-mono text-xs ${cur === n ? "bg-blue-500/20 text-white" : "text-slate-400 hover:text-white"}`}>{n}</button>)}
      </div>
      <pre className="flex-1 overflow-auto scroll-thin p-4 text-xs leading-relaxed text-slate-300 font-mono" data-testid="file-content">{files[cur]}</pre>
    </div>
  );
}

export const Empty = ({ children }) => <div className="h-full grid place-items-center text-sm text-slate-500 p-8 text-center">{children}</div>;

function CheckRow({ c }) {
  const Icon = c.ok ? Check : c.severity === "error" ? X : AlertTriangle;
  const color = c.ok ? "text-emerald-400" : c.severity === "error" ? "text-red-400" : "text-amber-400";
  return (
    <div className="flex gap-3 py-2 border-b border-white/5 last:border-0 text-sm" data-testid="quality-check-row">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
      <div><p className="text-slate-200">{c.name}</p>{!c.ok && <p className="text-xs text-slate-400 mt-0.5">{c.detail}</p>}</div>
    </div>
  );
}

export function QualityView({ version, onFix }) {
  if (!version) return <Empty>Genera la aplicación para ver el informe del verificador.</Empty>;
  const q = version.quality;
  const rt = version.runtime;
  return (
    <div className="h-full overflow-y-auto scroll-thin p-5 space-y-6" data-testid="quality-view">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-medium">Verificación estática · v{version.number}</h3>
          {q && <span className={`text-xs px-2 py-0.5 rounded-full border ${q.passed ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`} data-testid="quality-status">{q.passed ? "Superada" : "Con errores"}</span>}
        </div>
        {q ? <p className="text-xs text-slate-500 mt-1">Intentos de corrección: {q.attempt} de {q.max_attempts || q.attempt}</p> : <p className="text-xs text-slate-500 mt-1">Versión sin informe.</p>}
        <div className="mt-3">{q?.checks?.map((c) => <CheckRow key={c.name} c={c} />)}</div>
      </div>
      <div>
        <h3 className="text-white font-medium">Ejecución en entorno aislado</h3>
        <p className="text-xs text-slate-500 mt-1">iframe con origen opaco, sin acceso a red, a cookies ni a datos de la plataforma.</p>
        {!rt && <p className="text-sm text-slate-400 mt-3" data-testid="runtime-pending">Abre la vista previa para ejecutar la comprobación.</p>}
        {rt && (
          <div className="mt-3" data-testid="runtime-result">
            <p className={`text-sm ${rt.ok ? "text-emerald-300" : "text-red-300"}`}>{rt.ok ? "Sin errores de ejecución" : `${rt.errors.length} error(es) de ejecución`} · {fmtDate(rt.checked_at)}</p>
            {rt.errors.map((e, i) => <p key={i} className="font-mono text-xs text-red-200/80 mt-1">• {e}</p>)}
          </div>
        )}
      </div>
      {(q && !q.passed) || (rt && !rt.ok) ? (
        <button onClick={() => onFix([...(q?.errors || []), ...(rt?.errors || [])])} data-testid="quality-fix-btn"
          className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm">Enviar errores al programador</button>
      ) : null}
    </div>
  );
}

export function VersionsView({ versions, current, viewing, onView, onRestore }) {
  if (!versions.length) return <Empty>Sin versiones todavía.</Empty>;
  return (
    <div className="h-full overflow-y-auto scroll-thin p-4 space-y-2" data-testid="versions-view">
      {versions.map((v) => (
        <div key={v.number} className={`rounded-xl border p-3 ${viewing === v.number ? "border-cyan-500/50 bg-cyan-500/5" : "border-[#222B40]"}`} data-testid={`version-item-${v.number}`}>
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm text-white">v{v.number}{v.number === current && <span className="ml-2 text-[10px] text-emerald-300">actual</span>}</span>
            <div className="flex gap-1">
              <button onClick={() => onView(v.number)} data-testid={`version-view-${v.number}`} className="p-1.5 rounded text-slate-400 hover:text-white" title="Ver"><Eye className="w-4 h-4" /></button>
              {v.number !== current && <button onClick={() => onRestore(v.number)} data-testid={`version-restore-${v.number}`} className="p-1.5 rounded text-slate-400 hover:text-cyan-300" title="Restaurar"><RotateCcw className="w-4 h-4" /></button>}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{v.prompt}</p>
          <p className="text-[11px] text-slate-600 mt-1">{fmtDate(v.created_at)}{v.quality && ` · ${v.quality.passed ? "verificada" : "con errores"}`}</p>
        </div>
      ))}
    </div>
  );
}

export function SpecView({ spec }) {
  const sections = [["analysis", "Análisis (Agente Analista)"], ["architecture", "Arquitectura (Agente Arquitecto)"], ["design", "Diseño (Agente Diseñador)"], ["last_change", "Último cambio analizado"]];
  const present = sections.filter(([k]) => spec?.[k] && Object.keys(spec[k]).length);
  if (!present.length) return <Empty>Los agentes aún no han analizado el proyecto.</Empty>;
  return (
    <div className="h-full overflow-y-auto scroll-thin p-5 space-y-5" data-testid="spec-view">
      {present.map(([k, label]) => (
        <div key={k}>
          <h3 className="text-sm text-cyan-300 font-medium mb-2">{label}</h3>
          <pre className="text-xs font-mono text-slate-300 bg-[#0A0D14] border border-[#222B40] rounded-xl p-4 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(spec[k], null, 2)}</pre>
        </div>
      ))}
    </div>
  );
}
